import React, { createContext, type PropsWithChildren } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Anchor, type AnchorProps } from '@atlaskit/primitives/anchor';

import { type QueryParams } from '../../../common/types';
import { extend } from '../../../common/utils/object-utils';
import { defaultGenerateSearchUrl, defaultOnNavigate } from '../../../common/utils/url';

export type LinkComponentProps = AnchorProps;

interface AppContextData {
	/**
	 * A function that generates a search URL with the given query parameters.
	 */
	generateSearchUrl: (queryParams: QueryParams) => string;

	/**
	 * Optional component that is used to render links instead of a generic anchor tag.
	 */
	linkComponent: React.ComponentType<LinkComponentProps>;

	/**
	 * A function that maps a user's ID to the host application's profile page URL.
	 * When this returns null, the package knows that routing information is not
	 * available to reach a user's profile. UI may choose not to render links
	 * to user profiles then.
	 */
	mapUserIdToProfileUrl: (userId: string) => string | null;

	/**
	 * Function that is used when changing the browser's URL. If not provided,
	 * the default will be an assignment to window.lcoation.href.
	 */
	onNavigate: (url: string, action: 'push' | 'replace') => void;

	/**
	 * The query parameters that are currently in use.
	 */
	queryParams: QueryParams;
}

export type AppContextProviderProps = Partial<AppContextData>;

export const defaultAppContextData: AppContextData = {
	linkComponent: Anchor,
	mapUserIdToProfileUrl: () => null,
	generateSearchUrl: defaultGenerateSearchUrl,
	queryParams: {},
	onNavigate: defaultOnNavigate,
};

/**
 * Provides information about the host application that can't be stored in a state store,
 * e.g. a function.
 */
const AppContext = createContext<AppContextData>(defaultAppContextData);

export const useAppContext = (): AppContextData => React.useContext(AppContext);

export const AppContextProvider = ({
	children,
	...props
}: PropsWithChildren<AppContextProviderProps>): React.JSX.Element => (
	<AppContext.Provider value={extend(defaultAppContextData, props)}>{children}</AppContext.Provider>
);
